import React, { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Divider,
} from "@mantine/core";
import { notifySuccess } from "../../../utils/notify";
import { IconAlertCircle, IconUserPlus } from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import ClientLookup from "../../common/ClientLookup";

interface AttachGiftCardToClientModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  giftCardId: string;
  /** The card's code, shown in the title so staff know what they're assigning. */
  code: string;
  /** Reload the list after a successful attach. */
  onSuccess: () => void | Promise<void>;
}

/**
 * Assign an unclaimed gift card to a client from the Sales → Gift Cards drawer.
 * This is the inverse of the client-profile `AttachGiftCardModal` (which starts
 * from a client and searches for a card by code): here the card is known and
 * staff pick the client. Reuses the shared `ClientLookup` (existing-or-new
 * client) and the same `attachGiftCard` call as the post-checkout fork.
 */
export default function AttachGiftCardToClientModal({
  opened,
  onClose,
  salonId,
  giftCardId,
  code,
  onSuccess,
}: AttachGiftCardToClientModalProps) {
  const { isMobile } = useResponsive();
  const { attachGiftCard } = useApp();
  const [clientId, setClientId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setClientId("");
      setSubmitting(false);
      setError(null);
    }
  }, [opened]);

  const handleAttach = async () => {
    if (!clientId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await attachGiftCard(salonId, giftCardId, { client_id: clientId });
      notifySuccess("The gift card is now on the client's profile.", {
        title: "Gift card attached",
      });
      await Promise.resolve(onSuccess());
      onClose();
    } catch (err) {
      console.error("Failed to attach gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to attach the gift card. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Attach gift card {code}</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}
        <Text size="sm" c="dimmed">
          Assign this gift card to the client who will redeem it.
        </Text>
        <ClientLookup
          value={clientId}
          onChange={setClientId}
          error={undefined}
          disabled={submitting}
        />
        <Divider />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleAttach}
            loading={submitting}
            disabled={!clientId || submitting}
            leftSection={<IconUserPlus size={18} />}
          >
            Attach gift card
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
